import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TeamService } from '../../../../core/services/team.service';
import { Team } from '../../../../core/models/models';
import { ConfirmationDialogComponent } from '../../../../shared/components/dialogs/confirmation-dialog.component';

@Component({
  selector: 'app-team-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, ConfirmationDialogComponent],
  templateUrl: './team-dialog.component.html',
  styleUrl: './team-dialog.component.scss'
})
export class TeamDialogComponent implements OnInit {
  errorMessage = '';
  isDeleteConfirmOpen = false;
  canDeleteTeam = false;

  form: UntypedFormGroup;

  constructor(
    private fb: UntypedFormBuilder,
    private teamService: TeamService,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<TeamDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Team
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: ['']
    });
  }

  ngOnInit(): void {
    this.form.patchValue({
      name: this.data?.name ?? '',
      description: this.data?.description ?? ''
    });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload: Team = {
      ...this.data,
      name: this.form.value.name ?? '',
      description: this.form.value.description ?? ''
    };

    const request$ = payload.id
      ? this.teamService.updateTeam(payload.id, payload)
      : this.teamService.createTeam(payload);

    request$.subscribe({
      next: () => this.dialogRef.close(true),
      error: (err) => {
        this.errorMessage = err?.error?.message || err?.error || 'Erreur lors de la sauvegarde de l\'équipe';
        this.cdr.detectChanges();
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onRequestDelete(): void {
    if (!this.data?.id) {
      return;
    }
    this.teamService.canDeleteTeam(this.data.id).subscribe({
      next: (canDelete) => {
        this.canDeleteTeam = canDelete;
        this.isDeleteConfirmOpen = true;
        this.cdr.detectChanges();
      },
      error: () => {
        this.errorMessage = 'Impossible de vérifier le mode de suppression de l\'équipe';
        this.cdr.detectChanges();
      }
    });
  }

  confirmDelete(): void {
    if (!this.data?.id) {
      return;
    }
    this.teamService.deleteTeam(this.data.id).subscribe({
      next: () => this.dialogRef.close(true),
      error: (err) => {
        this.errorMessage = err?.error?.message || err?.error || 'Erreur lors de la suppression/archivage de l\'équipe';
        this.isDeleteConfirmOpen = false;
        this.cdr.detectChanges();
      }
    });
  }

  cancelDelete(): void {
    this.isDeleteConfirmOpen = false;
    this.cdr.detectChanges();
  }

  getDeleteDialogTitle(): string {
    return this.canDeleteTeam ? 'Supprimer équipe' : 'Archiver équipe';
  }

  getDeleteDialogLabel(): string {
    return this.canDeleteTeam ? 'Supprimer' : 'Archiver';
  }

  getDeleteDialogMessage(): string {
    const name = this.data?.name ?? '';
    if (this.canDeleteTeam) {
      return `Voulez-vous vraiment supprimer l'équipe "${name}" ? Cette action est irreversible.`;
    }
    return `L'équipe "${name}" est liée à des objets. Voulez-vous l'archiver ?`;
  }
}
